import appConfig from "@/configs/app-config";
import { TrackingEvent } from "../../constants/events";
import { pushToDataLayer } from "../../providers/google/utils";
import {
  buildHotelEcommerceItem,
  buildHotelRefundEcommerce,
  normalizeHotelEcommerceForDataLayer,
} from "./utils";

/**
 * Google Analytics - Hotel Provider
 * Hotel kategorisi için Google Analytics event'lerini yönetir
 */
export class HotelGoogleProvider {
  track(event, data) {
    switch (event) {
      case TrackingEvent.VIRTUAL_PV:
        this.trackVirtualPv(data);
        break;
      case TrackingEvent.SEARCH_RESULT:
        this.trackSearchResult(data);
        break;
      case TrackingEvent.NO_SEARCH_RESULT:
        this.trackNoSearchResult(data);
        break;
      case TrackingEvent.VIEW_ITEM_LIST:
        this.trackViewItemList(data);
        break;
      case TrackingEvent.SELECT_ITEM:
        this.trackSelectItem(data);
        break;
      case TrackingEvent.VIEW_ITEM:
        this.trackViewItem(data);
        break;
      case TrackingEvent.ADD_TO_CART:
        this.trackAddToCart(data);
        break;
      case TrackingEvent.BEGIN_CHECKOUT:
        this.trackBeginCheckout(data);
        break;
      case TrackingEvent.ADD_TO_WISHLIST:
        this.trackAddToWishlist(data);
        break;
      case TrackingEvent.ADD_PAYMENT_INFO:
        this.trackAddPaymentInfo(data);
        break;
      case TrackingEvent.PURCHASE:
        this.trackPurchase(data);
        break;
      case TrackingEvent.REFUND:
        this.trackRefund(data);
        break;
      case TrackingEvent.FILTER_APPLIED:
        this.trackFilterApplied(data);
        break;
      case TrackingEvent.SORTING_APPLIED:
        this.trackSortingApplied(data);
        break;
      case TrackingEvent.HOTEL_MAP_VIEW:
        this.trackHotelMapView(data);
        break;
      case TrackingEvent.CHECK_RESERVATION:
        this.trackCheckReservation(data);
        break;
      case TrackingEvent.CALL_TO_CANCEL_RESERVATION:
        this.trackCallToCancelReservation(data);
        break;
      case TrackingEvent.SUPPORT_TO_CANCEL_RESERVATION:
        this.trackSupportToCancelReservation(data);
        break;
      case TrackingEvent.PROMOTIONAL_BANNER_CLICKED:
        this.trackPromotionalBannerClicked(data);
        break;
      case TrackingEvent.SELECT_COUPON:
      case TrackingEvent.VIEW_COUPON:
      case TrackingEvent.COUPON_APPLIED:
        this.trackCouponEvent(event, data);
        break;
      default:
        this.trackGenericEvent(event, data);
    }
  }

  trackVirtualPv(data) {
    pushToDataLayer(TrackingEvent.VIRTUAL_PV, {
      environment: data.environment || "prod",
      virtual_page_url: data.virtual_page_url,
      page_type: data.page_type,
      hotel_city: data.hotel_city ?? null,
      hotel_region: data.hotel_region ?? null,
      hotel_country: data.hotel_country ?? null,
      hotel_category: data.hotel_category ?? null,
      hotel_category2: data.hotel_category2 ?? null,
      language: data.language ?? "TR",
      turna_session_id: data.turna_session_id ?? null,
      turna_device_id: data.turna_device_id ?? null,
      website_version: appConfig.WEBSITE_VERSION,
      version: appConfig.WEBSITE_VERSION,
      user_id: data.user_id.toString() ?? "",
      user_hashed_email: data.user_hashed_email ?? null,
      user_hashed_phone: data.user_hashed_phone ?? null,
      user_type: data.user_type ?? null,
      guest_user_hashed_email: data.guest_user_hashed_email ?? null,
      guest_user_hashed_phone: data.guest_user_hashed_phone ?? null,
      // user_mail: data.user_mail ?? null,
      // user_phone: data.user_phone ?? null,
      user_turna_points: data.user_turna_points ?? null,
      user_promotions_consent: data.user_promotions_consent ?? null,
      first_time_purchaser: data.first_time_purchaser ?? null,
    });
  }

  trackSearchResult(data) {
    pushToDataLayer(TrackingEvent.SEARCH_RESULT, {
      event: "custom_event",
      event_category: "search",
      event_action: "search_result",
      search_category: "hotel",
      search_destination: data.search_destination,
      search_result_number: data.search_result_number,
    });
  }

  trackNoSearchResult(data) {
    pushToDataLayer(TrackingEvent.NO_SEARCH_RESULT, {
      event: "custom_event",
      event_category: "search",
      event_action: "no_search_result",
      search_category: "hotel",
      search_term: data?.search_term ?? null,
      hotel_city: data?.hotel_city ?? null,
      hotel_district: data?.hotel_district ?? null,
      hotel_neighborhood: data?.hotel_neighborhood ?? null,
      hotel_country: data?.hotel_country ?? null,
      hotel_region: data?.hotel_region ?? null,
      state: data?.state ?? null,
      guest_total: data?.guest_total ?? null,
      guest_adult_number: data?.guest_adult_number ?? null,
      guest_children_number: data?.guest_children_number ?? null,
      start_date: data?.start_date ?? null,
      end_date: data?.end_date ?? null,
    });
  }

  trackViewItemList(data) {
    // Önceki ecommerce datasını temizle
    pushToDataLayer({ ecommerce: null });

    // Eğer caller doğrudan ecommerce objesini verdiyse aynen geçir
    let ecommerce = data?.ecommerce;

    // Aksi halde products dizisinden items üret
    if (!ecommerce) {
      const products = Array.isArray(data?.products)
        ? data.products
        : data?.product
          ? [data.product]
          : [];

      const items = products.flatMap((product, idx) =>
        buildHotelEcommerceItem({
          product,
          index:
            typeof data?.startIndex === "number"
              ? data.startIndex + idx
              : idx + 1,
          item_list_id: data?.item_list_id || "hotel_search",
          item_list_name: data?.item_list_name || "Hotel Search",
          cross_product_promotion: data?.cross_product_promotion,
        }),
      );

      ecommerce = {
        item_list_id: data?.item_list_id || "hotel_search",
        item_list_name: data?.item_list_name || "Hotel Search",
        passengers: data?.passengers, // varsa gönderilir
        items,
      };
    }
    ecommerce = normalizeHotelEcommerceForDataLayer(ecommerce);

    pushToDataLayer(TrackingEvent.VIEW_ITEM_LIST, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item_list",
      ecommerce,
    });
  }

  trackSelectItem(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.SELECT_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "select_item",
      ecommerce: normalizeHotelEcommerceForDataLayer(data.ecommerce),
    });
  }

  trackViewItem(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.VIEW_ITEM, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "view_item",
      ecommerce: normalizeHotelEcommerceForDataLayer(data.ecommerce),
    });
  }

  trackAddToCart(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_TO_CART, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_to_cart",
      ecommerce: normalizeHotelEcommerceForDataLayer(data.ecommerce),
    });
  }

  trackBeginCheckout(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.BEGIN_CHECKOUT, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "begin_checkout",
      ecommerce: normalizeHotelEcommerceForDataLayer(data?.ecommerce),
    });
  }

  trackAddToWishlist(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_TO_WISHLIST, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_to_wishlist",
      ecommerce: normalizeHotelEcommerceForDataLayer(data?.ecommerce),
    });
  }

  trackAddPaymentInfo(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.ADD_PAYMENT_INFO, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "add_payment_info",
      ecommerce: data.ecommerce,
    });
  }

  trackPurchase(data) {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.PURCHASE, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "purchase",
      ecommerce: data.ecommerce,
    });
  }

  trackRefund(data) {
    const ecommerce =
      data.ecommerce ??
      buildHotelRefundEcommerce({
        products: data.products,
        basketData: data.basketData,
        priceSummary: data.priceSummary,
        currency: data.currency,
      });

    if (!ecommerce) return;

    pushToDataLayer({ ecommerce: null });
    pushToDataLayer(TrackingEvent.REFUND, {
      event: "ecom_event",
      event_category: "ecommerce",
      event_action: "refund",
      ecommerce,
    });
  }

  trackFilterApplied(data) {
    pushToDataLayer(TrackingEvent.FILTER_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "filter_applied",
      filter_product_category: "hotel",
      filter_category: data?.filterName,
      filter_subcategory: data?.label,
      sorting_type: `${data?.sort?.SortBy}_${data?.sort?.Ascending ? "asc" : "desc"}`,
    });
  }

  trackSortingApplied(data) {
    pushToDataLayer(TrackingEvent.SORTING_APPLIED, {
      event: "custom_event",
      event_category: "filtering",
      event_action: "sorting_applied",
      filter_product_category: "hotel",
      filter_category: "sorting",
      filter_subcategory: `${data?.sortBy}_${data?.ascending ? "asc" : "desc"}`,
    });
  }

  trackCheckReservation(data) {
    const pnr =
      data?.pnr_number != null && String(data?.pnr_number).trim() !== ""
        ? data?.pnr_number
        : null;

    pushToDataLayer(TrackingEvent.CHECK_RESERVATION, {
      event: "custom_event",
      event_category: "reservation",
      event_action: "check_reservation",
      pnr_number: pnr,
      reservation_category: "hotel",
    });
  }

  trackCallToCancelReservation(data) {
    pushToDataLayer(TrackingEvent.CALL_TO_CANCEL_RESERVATION, {
      event: "custom_event",
      event_category: "custom_event",
      event_action: "call_to_cancel_reservation",
      pnr_number: data?.pnr_number,
      reservation_category: "hotel",
    });
  }

  trackSupportToCancelReservation(data) {
    pushToDataLayer(TrackingEvent.SUPPORT_TO_CANCEL_RESERVATION, {
      event: "custom_event",
      event_category: "custom_event",
      event_action: "support_to_cancel_reservation",
      pnr_number: data?.pnr_number,
      productType: data?.productType,
      reservation_category: "hotel",
    });
  }

  trackHotelMapView(data) {
    pushToDataLayer("custom_event", {
      event: "custom_event",
      event_category: "custom_event",
      event_action: "hotel_map_view",
      hotel_city: data?.hotel_city ?? null,
      hotel_district: data?.hotel_district ?? null,
      hotel_neighborhood: data?.hotel_neighborhood ?? null,
      hotel_country: data?.hotel_country ?? null,
      hotel_region: data?.hotel_region ?? null,
      state: data?.state ?? null,
      guest_total: data?.guest_total ?? null,
      guest_adult_number: data?.guest_adult_number ?? null,
      guest_children_number: data?.guest_children_number ?? null,
      start_date: data?.start_date ?? null,
      end_date: data?.end_date ?? null,
    });
  }

  trackPromotionalBannerClicked(data) {
    pushToDataLayer(TrackingEvent.PROMOTIONAL_BANNER_CLICKED, {
      event: "custom_event",
      event_category: "switch",
      event_action: `switch_to_${data?.type}`,
      [`${data?.type}_route`]: `${data?.from}-${data?.to}`,
      hotel_route: `${data?.fromCity}-${data?.toCity}`,
    });
  }

  trackCouponEvent(event, data) {
    // Extract coupon data from raw offer object
    const offer = data.offer || {};
    const currencyCode = data.currencyCode || "TRY";

    const couponName = offer?.code || "";
    const couponValue = offer?.discountText || "";

    pushToDataLayer(event, {
      event: "custom_event",
      event_category: "coupon",
      event_action: event,
      coupon_name: couponName,
      coupon_value: null,
      coupon_currency: currencyCode,
      coupon_category: "hotel",
    });
  }

  trackGenericEvent(event, data) {
    pushToDataLayer(event, data);
  }
}
